/**
 * MIC-5 — local JSON draft of an in-progress inventory count.
 * Survives spotty network; downloadable as BULKINV-YYYYMMDD.json until
 * submit succeeds. Not a full sync queue — recovery is download + re-enter.
 */
import type {
  MfgInventoryCountLine,
  MfgInventoryCountSession,
} from "@/lib/manufacturing/types/inventoryCount";

export const INVENTORY_COUNT_DRAFT_VERSION = 1 as const;

export type InventoryCountOfflineDraft = {
  version: typeof INVENTORY_COUNT_DRAFT_VERSION;
  saved_at: string;
  organization_id: string;
  plant_id: string;
  session: MfgInventoryCountSession;
  lines: MfgInventoryCountLine[];
};

function storageKey(organizationId: string, plantId: string): string {
  return `mfg:inventory-count-draft:v1:${organizationId}:${plantId}`;
}

export function bulkInvDownloadFilename(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `BULKINV-${y}${m}${d}.json`;
}

export function buildInventoryCountOfflineDraft(input: {
  organizationId: string;
  plantId: string;
  session: MfgInventoryCountSession;
  lines: MfgInventoryCountLine[];
  savedAt?: string;
}): InventoryCountOfflineDraft {
  return {
    version: INVENTORY_COUNT_DRAFT_VERSION,
    saved_at: input.savedAt ?? new Date().toISOString(),
    organization_id: input.organizationId,
    plant_id: input.plantId,
    session: input.session,
    lines: input.lines,
  };
}

export function saveInventoryCountOfflineDraft(draft: InventoryCountOfflineDraft): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      storageKey(draft.organization_id, draft.plant_id),
      JSON.stringify(draft),
    );
  } catch {
    // Quota / private mode — ignore; download still works from in-memory draft.
  }
}

export function loadInventoryCountOfflineDraft(
  organizationId: string,
  plantId: string,
): InventoryCountOfflineDraft | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(storageKey(organizationId, plantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InventoryCountOfflineDraft;
    if (parsed?.version !== INVENTORY_COUNT_DRAFT_VERSION) return null;
    if (!parsed.session || !Array.isArray(parsed.lines)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearInventoryCountOfflineDraft(
  organizationId: string,
  plantId: string,
): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(storageKey(organizationId, plantId));
  } catch {
    // ignore
  }
}

export function downloadInventoryCountOfflineDraft(
  draft: InventoryCountOfflineDraft,
  filename = bulkInvDownloadFilename(),
): void {
  if (typeof document === "undefined") return;
  const blob = new Blob([JSON.stringify(draft, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
